import { apiClient } from "@/api/client";
import { Options } from "@/components/ui/DropDown";
import { AxiosError } from "axios";
import { create } from "zustand";

interface DashboardState {
  campaignData: Array<CampaignData>;
  addCampaignData: (newData: CampaignData) => void;

  getCampaign: () => Promise<void>;
}

export interface CampaignDetailResponse {
  success: boolean;
  data: CampaignData;
}

export interface CampaignResponse {
  success: boolean;
  data: CampaignData[];
}

export interface CampaignData {
  campaign: Campaign;
  teams: Team[];
  users: User[];
  languages: Language[];
  header_image?: {
    id: string;
    key: string | null;
    mime: string;
    type: string;
    url: string; // ✅ presigned temporary URL
  };
}


export interface Campaign {
  id: string;
  name: string;
  project_code: string;
  budget: number;
  file_naming_convention: string;
  campaign_poster: string | null;
  status: string;
  progress_status: "campaign" | "flight" | "assets" | "done" | "brief" | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface Team {
  campaign_team_id: string;
  team_id: string;
  name: string;
  team_type: "client" | "media" | "creative" | string;
  all_users: User[];
  campaign_users: User[];
}

export interface User {
  team_id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  profile_picture: string | null;
}

export interface Language {
  campaign_language_id: string;
  language_id: string;
  name: string;
}

const useDashboardStore = create<DashboardState>((set, get) => ({
  campaignData: [],

  addCampaignData: (newData: CampaignData) => {
    set((state) => ({
      campaignData: [...state.campaignData, newData],
    }));
  },

  getCampaign: async () => {
    try {
      const response = await apiClient.get<CampaignResponse>("/campaigns");
      const data = response.data;

      set({ campaignData: data.data });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },
}));

export default useDashboardStore;
